import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AudioService } from '../../shared/services/audio.service';
import { MicrophoneService } from '../../shared/services/microphone.service';
import { VoiceCallService } from '../../shared/services/voice-call.service';
import { TokensService } from '../../shared/services/tokens.service';

import { ToastComponent } from '../../shared/components/toast/toast.component';
import { PrimerModalComponent } from './primer-modal/primer-modal.component';

import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { IToastConfig } from '../../shared/interfaces/toast-config.interfaces';
import { ICreatorDiscoverData } from '../../interfaces/creator.interfaces';

import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';
import { EMicStatus, ECallButton, ECallStatus } from '../../enums/voice-call.enums';
import { EToastType, EToastPosition, EToastButtonTitle } from '../../shared/enums/toast.enums';

import { ESvgTypes } from 'app/shared/enums/svg.enums';

import { TimeFormatPipe } from 'app/pipes/time-format.pipe';
import { SUPPORT_LINK } from '../../shared/const/app.constants';

declare const vad: VadWeb.VADNamespace;

// Extend Window interface for webkit audio context
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

@Component({
  selector: 'stxt-voice-call',
  standalone: true,
  imports: [CommonModule, ToastComponent, SvgIconComponent, TimeFormatPipe],
  templateUrl: './voice-call.component.html',
  styleUrl: './voice-call.component.scss'
})
export class VoiceCallComponent implements OnInit, OnDestroy {
  @Input() creatorData: ICreatorDiscoverData;
  @Input() userId: string;
  @Input() tokensBalance: number;
  @Output() closeVoiceCall: EventEmitter<void> = new EventEmitter<void>();
  public isPrimerModal: boolean = true;
  public lowTokensBalance: boolean = false;
  public isMuted: boolean = false;
  public voiceCallView: string = 'main';
  public toastConfig: IToastConfig = {
    toastHeading: '',
    toastSubheading: '',
    toastType: EToastType.INFO,
    position: EToastPosition.TOP,
    isBanner: true
  };
  public microphoneSvg: ISvgConfig = { name: 'microphone', fill: ESvgTypes.Tonal };
  public endCallSvg: ISvgConfig = { name: 'end_call', fill: ESvgTypes.Tonal };
  public unmuteSvg: ISvgConfig = { name: 'unmute', fill: ESvgTypes.Tonal };
  public warningSvg: ISvgConfig = { name: 'error', fill: ESvgTypes.None };
  public callTime: Subscription;
  public creatorResponse: string = '';
  public callDuration: number = 0;
  private callStartTime: number = 0;
  protected readonly EMicStatus = EMicStatus;
  private vadInstance!: VadWeb.MicVAD;
  public currentVolume: number = 0;
  public isSpeaking: boolean = false;
  public voiceCallState: string = ECallStatus.CALLING;
  public creatorResponseVolume: number = 0;
  public callPrice: number = 4;
  public isCreatorSpeaking: boolean = false;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private creatorAudioContext: AudioContext | null = null;
  private activeModalRef: NgbModalRef | null = null;
  private lowBalanceTimer: ReturnType<typeof setTimeout> | null = null;
  private remainingTime: number = 60;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private ringingAudio?: HTMLAudioElement;
  private isConnected: boolean = false;
  private userStream: MediaStream | null = null;

  private statusSubscription: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly modal: NgbModal,
    private readonly microphoneService: MicrophoneService,
    private readonly voiceCallService: VoiceCallService,
    private readonly audioService: AudioService,
    private readonly tokensService: TokensService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.microphoneService.init();
    this.subscribeToStatusChanges();
    await this.enterCall();
    this.listenToRouteChanges();
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.callTime?.unsubscribe();
    this.vadInstance?.pause();
    this.stopAudioMonitoring();
  }

  private subscribeToStatusChanges(): void {
    this.statusSubscription = this.microphoneService.status$.subscribe((status: EMicStatus) => {
      this.voiceCallView = status;

      if (status === EMicStatus.GRANTED) {
        this.activeModalRef = this.modal.open(PrimerModalComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false
        });
        this.activeModalRef.componentInstance.tokensBalance = this.tokensBalance;
        this.activeModalRef.closed.subscribe((res: ECallButton) => {
          this.handlePrimerModalAction(res);
          this.isPrimerModal = false;
        });
        return;
      }
      this.handleMicStatus(status);
    });
  }

  private handlePrimerModalAction(action: ECallButton): void {
    switch (action) {
      case ECallButton.CALL_NOW:
        this.initCallConnection();
        break;
      case ECallButton.CALL_LATER:
        this.endCall();
        break;
      default:
        this.endCall();
        break;
    }
  }

  private handleMicStatus(status: EMicStatus): void {
    if (status !== EMicStatus.PROMPT && status !== EMicStatus.UNKNOWN) {
      this.toastConfig = this.initToastContent(status);
      this.cdr.detectChanges();
    }
  }

  private playRinging(): void {
    this.stopRinging();

    this.ringingAudio = new Audio('https://cdn.pixabay.com/audio/2023/05/30/audio_200d0b46bc.mp3');
    this.ringingAudio.loop = true;
    this.ringingAudio.volume = 0.5;
    this.ringingAudio.play();
  }

  private stopRinging(): void {
    if (!this.ringingAudio) return;
    this.ringingAudio.pause();
    this.ringingAudio.currentTime = 0;
    this.ringingAudio = null;
  }

  async enterCall(): Promise<void> {
    const status = this.microphoneService.getLastStatus();
    this.voiceCallView = status;

    this.handleMicStatus(status);

    await this.microphoneService.requestAccess();
  }

  buttonClicked(buttonTitle: string): void {
    if (buttonTitle === EToastButtonTitle.CONTACT_SUPPORT) {
      window.open(SUPPORT_LINK, '_blank');
      return;
    }
    this.onTryAgain();
  }

  async onTryAgain(): Promise<void> {
    this.microphoneService.resetStored();
    await this.microphoneService.requestAccess();
  }

  initToastContent(toastCase: string): IToastConfig {
    switch (toastCase) {
      case EMicStatus.DENIED:
        return {
          ...this.toastConfig,
          toastHeading: 'Allow microphone access',
          toastSubheading: `Enable microphone permissions to voice call ${this.creatorData.name}.`,
          buttonPrimary: EToastButtonTitle.ENABLE_ACCESS,
          buttonSecondary: EToastButtonTitle.TRY_AGAIN,
          toastType: EToastType.INFO
        };
      case EMicStatus.ERROR:
        return {
          ...this.toastConfig,
          toastHeading: 'Unable to start voice call',
          toastSubheading: 'Try again in a moment or contact support if issues persist.',
          buttonPrimary: EToastButtonTitle.TRY_AGAIN,
          buttonSecondary: EToastButtonTitle.CONTACT_SUPPORT,
          toastType: EToastType.ERROR_RED
        };
      case EMicStatus.NO_DEVICE:
        return {
          ...this.toastConfig,
          toastHeading: 'No microphone detected',
          toastSubheading: 'Check microphone connection and system settings, then refresh page.',
          buttonPrimary: EToastButtonTitle.REFRESH_NOW,
          buttonSecondary: EToastButtonTitle.TRY_AGAIN,
          toastType: EToastType.ERROR_RED
        };
      default:
        return null;
    }
  }

  async activateVAD(): Promise<void> {
    try {
      this.vadInstance = await vad.MicVAD.new({
        getStream: async () => {
          this.userStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              channelCount: 1,
              sampleRate: 44100,
              echoCancellation: false,
              autoGainControl: false,
              noiseSuppression: true
            }
          });
          return this.userStream;
        },
        onSpeechStart: () => {
          if (this.voiceCallState !== ECallStatus.LISTENING && this.isMuted) return;
          this.voiceCallState = ECallStatus.LISTENING;
          this.isSpeaking = true;
          this.cdr.detectChanges();
        },
        onSpeechEnd: (audio: Float32Array) => {
          if (this.voiceCallState !== ECallStatus.LISTENING && this.isMuted) return;
          this.voiceCallState = ECallStatus.THINKING;
          this.isSpeaking = false;
          this.convertVADAudioToBase64(audio);
          this.currentVolume = 0;
          this.cdr.detectChanges();
        },
        onnxWASMBasePath: 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/',
        baseAssetPath: 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.27/dist/'
      });

      this.vadInstance.start();

      // Start volume monitoring
      this.startVolumeMonitoring();
    } catch (error) {
      console.warn('VAD initialization failed:', error);
    }
  }

  convertVADAudioToBase64(audio: Float32Array): void {
    const inputSampleRate = 16000;
    const targetSampleRate = 44100;

    //resample to 44.1k
    const resampled = this.audioService.resampleBuffer(audio, inputSampleRate, targetSampleRate);

    //convert to 16-bit PCM bytes (Uint8Array)
    const pcmBytes = this.audioService.floatTo16BitPCM(resampled);

    //create 1 second of silence (44.1k samples * 2 bytes = 88200 bytes)
    const silenceBytes = new Uint8Array(88200); // automatically zero-filled

    //combine audio + silence
    const combined = new Uint8Array(pcmBytes.length + silenceBytes.length);
    combined.set(pcmBytes, 0);
    combined.set(silenceBytes, pcmBytes.length);

    //chunk into 8820-byte pieces
    const CHUNK_SIZE = 8820;
    const chunks = this.audioService.chunkUint8Array(combined, CHUNK_SIZE);

    //base64 encode and send each chunk
    for (const chunk of chunks) {
      const base64Chunk = this.audioService.uint8ToBase64(chunk);
      this.voiceCallService.streamAudio(base64Chunk);
    }
  }

  private startVolumeMonitoring(): void {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      this.mediaStream = stream;
      const analyser = this.audioContext!.createAnalyser();
      const microphone = this.audioContext!.createMediaStreamSource(stream);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      const updateVolume = () => {
        if (this.isSpeaking && !this.isMuted) {
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          const rawVolume = Math.min(average / 8, 1);
          this.currentVolume = Math.max(rawVolume, 0.1);
          this.cdr.detectChanges();
        } else if (this.isMuted) {
          this.currentVolume = 0;
          this.isSpeaking = false;
          this.cdr.detectChanges();
        }
        requestAnimationFrame(updateVolume);
      };

      updateVolume();
    });
  }

  private stopAudioMonitoring(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.creatorAudioContext) {
      this.creatorAudioContext.close();
      this.creatorAudioContext = null;
    }
  }

  public playCreatorResponse(): void {
    this.isCreatorSpeaking = true;
    this.voiceCallState = ECallStatus.AI_READY;

    const audioUrl = this.audioService.base64PcmToWav(this.creatorResponse);
    const audio = new Audio(audioUrl);
    let currentTime = 0;
    const duration = 5000; // Assume 5 second audio

    const simulateVolume = (): void => {
      if (this.isCreatorSpeaking && !audio.paused) {
        const progress = currentTime / duration;
        const baseVolume = 0.3 + Math.sin(progress * Math.PI * 4) * 0.2;
        const randomVariation = (this.secureRandom() - 0.5) * 0.3;
        this.creatorResponseVolume = Math.max(0.1, Math.min(1, baseVolume + randomVariation));

        currentTime += 100;
        this.cdr.detectChanges();
      } else {
        this.creatorResponseVolume = 0;
        this.cdr.detectChanges();
      }
    };

    // Start volume simulation
    const volumeInterval = setInterval(simulateVolume, 100);

    audio.play();

    audio.onended = () => {
      this.cleanState(volumeInterval);
    };

    audio.onerror = () => {
      this.cleanState(volumeInterval);
    };
  }

  private cleanState(volumeInterval: NodeJS.Timeout): void {
    clearInterval(volumeInterval);
    this.isCreatorSpeaking = false;
    this.creatorResponseVolume = 0;
    this.voiceCallState = ECallStatus.LISTENING;
    this.cdr.detectChanges();
  }

  private secureRandom(): number {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] / (0xffffffff + 1);
  }

  async initCallConnection(): Promise<void> {
    const request = {
      creator_id: this.creatorData.id
    };
    setTimeout(() => this.playRinging(), 300);
    this.voiceCallService.createCall(request).subscribe({
      next: res => {
        this.voiceCallService.connectToCallStream(res.id);
        this.voiceCallService.startCall();
        this.initCallSubscription();
      },
      error: () => {
        this.closeVoiceCall.emit();
      }
    });
  }

  onStartCall(): void {
    this.stopRinging();
    this.startCallTimer();
    this.lowTokensBalance = this.tokensBalance <= this.callPrice;
    this.isConnected = true;
    if (!this.lowTokensBalance) return;
    this.lowBalanceCountdown();
  }

  initCallSubscription(): void {
    this.voiceCallService.agentAudio$.subscribe(base64Audio => {
      if (!this.isConnected) {
        this.onStartCall();
      }
      this.voiceCallState = ECallStatus.AI_READY;
      this.creatorResponse = base64Audio;
      this.playCreatorResponse();
    });
    this.voiceCallService.lowBalance$.subscribe(amount => {
      this.lowTokensBalance = amount <= this.callPrice;
      if (amount <= this.callPrice && amount !== 0) {
        this.lowBalanceCountdown();
      } else if (amount === 0) {
        this.endCall();
      }
      this.cdr.detectChanges();
    });
  }

  lowBalanceCountdown(): void {
    this.remainingTime = 60;
    this.intervalId = setInterval(() => {
      this.remainingTime--;
      this.cdr.detectChanges();

      if (this.remainingTime <= 0) {
        this.endCall();
        this.clearLowBalanceTimer();
      }
    }, 1000);

    this.lowBalanceTimer = setTimeout(() => {
      this.endCall();
      this.clearLowBalanceTimer();
    }, 60000);
  }

  private clearLowBalanceTimer(): void {
    clearTimeout(this.lowBalanceTimer);
    this.lowBalanceTimer = null;
    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  private startCallTimer(): void {
    this.activateVAD();
    this.callStartTime = Date.now();

    this.callTime = interval(1000).subscribe(() => {
      this.callDuration = Math.floor((Date.now() - this.callStartTime) / 1000);
    });
  }

  private listenToRouteChanges(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => {
      if (!this.activeModalRef) return;
      this.activeModalRef.close();
    });
  }

  public endCall(): void {
    this.stopRinging();
    this.voiceCallService.endCall();
    this.callTime?.unsubscribe();
    this.closeVoiceCall.emit();
    this.tokensService.fetchTokensBalance(true).subscribe();
  }

  public toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.userStream.getAudioTracks().forEach(track => {
      track.enabled = !this.isMuted;
    });
    this.cdr.detectChanges();
  }
}
